import React from 'react';
import { DataState, Data, makeLoadingData } from '../../types/data-providers';
import useStateWhileMounted from '../../hooks/use-state-while-mounted';
import { useAuthHelper, useAuthState, getUserId } from '../AuthContextProvider';
import { AppDataLoader, AppData } from '../../lib/app-data-loader';

type ContextState = Data<AppData>;

const Context = React.createContext<ContextState | null>(null);

function useAppData(): ContextState {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useAppData must be used within a AppDataProvider');
    }
    return contextState;
}

const initialState: ContextState = { state: DataState.Loading };

const AppDataProvider: React.FC = (props) => {
    const [state, setState] = useStateWhileMounted<ContextState>(initialState);
    const authState = useAuthState();
    const userId = getUserId(authState);
    const authHelper = useAuthHelper();

    React.useEffect(() => {
        if (!userId) {
            return;
        }

        (async (): Promise<void> => {
            const result = await AppDataLoader.loadDataForUser(authHelper, userId);
            if (result.ok) {
                setState({
                    state: DataState.Loaded,
                    value: result.value,
                });
            } else {
                setState({
                    state: DataState.Error,
                    errorMessage: result.message || 'Unable to load application data',
                });
            }
        })();
    }, [userId, setState, authHelper]);

    return <Context.Provider value={state}>{props.children}</Context.Provider>;
};

const TestAppDataProvider: React.FC<{ value?: ContextState }> = (props) => (
    <Context.Provider value={props.value || makeLoadingData()}>{props.children}</Context.Provider>
);

export { useAppData, AppDataProvider, TestAppDataProvider };
